import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "primereact/tooltip";
import { Controller, useFormContext } from "react-hook-form";
import { countryPhoneCodes } from "~/utils/countries";
import { DirectorInformation } from "../../types/director";
import CustomeButton from "~/components/Button";
import AdminOnly from "~/components/AdminOnly";
import { useEffect } from "react";
import DirectorMatchingResult from "./director-matching-results";
import AuditSidebar from "~/components/audit-sidebar/AuditSidebar";
import API_ROUTES_V2 from "~/api-routes-v2";
import { UserRole } from "~/types/roles";
import { useSelector } from "react-redux";

interface DirectorInformationProps {
  isEditMode?: boolean;
  onSave?: () => void;
  isSaving?: boolean;
  isEditing?: boolean;
  setIsEditing?: (value: boolean) => void;
  directorsArr?: { data: Array<DirectorInformation>; loading: boolean };
  setIsSubmittedSuccessfully?: (value: boolean) => void;
  setIsAuditSidebarVisible?: (value: boolean) => void;
  isAuditSidebarVisible?: boolean;
  directorId?: string;
}
const DirectorInformationForm = ({
  isEditMode = false,
  onSave,
  isSaving = false,
  isEditing = false,
  setIsEditing,
  directorsArr,
  setIsSubmittedSuccessfully,
  setIsAuditSidebarVisible,
  isAuditSidebarVisible,
  directorId,
}: DirectorInformationProps) => {
  const {
    control,
    formState: { errors, isDirty, isValid },
    reset,
  } = useFormContext<DirectorInformation>();
  const isDisabled = isEditMode && !isEditing;
  const canSave = isDirty && isValid;
  const { role } = useSelector((state) => state.login);

  const auditLabels = () => {
    return {
      create: "Director Created",
      update: "Information Updated",
      delete: "Director Deleted",
    };
  };

  const phoneCodeOptions = countryPhoneCodes.map((item) => ({
    label: `${item.dial_code} ${item.name}`,
    value: item.dial_code.replace("+", ""),
  }));

  const handleEnableEdit = () => {
    setIsEditing?.(true);
  };

  const handleSave = () => {
    onSave?.();
  };

  const handleDiscard = () => {
    reset();
    setIsEditing?.(false);
  };

  useEffect(() => {
    if (isEditMode) {
      handleDiscard();
    }
  }, []);

  return (
    <div className="director-form flex flex-column gap-2">
      <div className="flex justify-content-between align-items-start">
        <div className="flex flex-column gap-2">
          <p className="form-title m-0"> Director Information </p>
          <div className="form-description m-0">
            {isEditMode
              ? "Find all personal information related to this appointment"
              : "Enter all the details for this appointment"}
          </div>
        </div>
        <AdminOnly>
          {isEditMode && (
            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <CustomeButton
                    label="Discard"
                    className="director-discard-btn"
                    onClick={handleDiscard}
                  />
                  <CustomeButton
                    label="Save Changes"
                    className={`director-save-btn ${!canSave ? "disabled" : ""}`}
                    onClick={handleSave}
                    icon={isSaving ? "pi pi-spin pi-spinner" : undefined}
                    disabled={!canSave || isSaving}
                  />
                </>
              ) : (
                <>
                  <CustomeButton
                    label="View History"
                    variant="outlined"
                    icon="pi pi-history mb-1 ml-2"
                    iconPos="right"
                    onClick={() => setIsAuditSidebarVisible?.(true)}
                  />
                  <CustomeButton
                    label="Edit"
                    className="director-edit-btn"
                    icon="pi pi-pencil"
                    iconPos="right"
                    onClick={handleEnableEdit}
                  />
                </>
              )}
            </div>
          )}
        </AdminOnly>
      </div>
      <div className="row flex gap-3">
        <div className="flex flex-column col-6 pl-0 pr-0  gap-2">
          <label htmlFor="given_name" className="flex align-items-center gap-1">
            Given name(s)*
            <span id="given-name-info" className="pi pi-info-circle"></span>
            <Tooltip
              target="#given-name-info"
              content="This is usually the person's first name or the name they usually go by."
              position="right"
              className="custom-tooltip tooltip-custom-format"
            />
          </label>
          <Controller
            name="given_name"
            control={control}
            rules={{
              required: "Given Name is required",
              pattern: {
                value: /^[A-Za-z\s]+$/,
                message: "Special characters are not allowed.",
              },
            }}
            render={({ field }) => (
              <InputText
                {...field}
                placeholder="Enter given name(s)"
                type="text"
                className={`input-field appearance-none outline-none w-full ${
                  errors.given_name ? "error" : ""
                }`}
                disabled={isDisabled}
              />
            )}
          />
          {errors.given_name && (
            <small className="error-message">{errors.given_name.message}</small>
          )}
        </div>
        <div className="flex flex-column col-6 pl-0 pr-0  gap-2">
          <label htmlFor="other_name" className="flex align-items-center gap-1">
            Other name(s)
            <span id="other-name-info" className="pi pi-info-circle"></span>
            <Tooltip
              target="#other-name-info"
              content="Any middle or extra names that the employee has. You can leave this blank if it doesn’t apply."
              position="right"
              className="custom-tooltip tooltip-custom-format"
            />
          </label>
          <Controller
            name="other_name"
            control={control}
            rules={{
              pattern: {
                value: /^[A-Za-z\s]+$/,
                message: "Special characters are not allowed.",
              },
            }}
            render={({ field }) => (
              <InputText
                {...field}
                placeholder="Enter any other names, such as middle names or clan names"
                type="text"
                className={`input-field appearance-none outline-none w-full ${
                  errors.other_name ? "error" : ""
                }`}
                disabled={isDisabled}
              />
            )}
          />
          {errors.other_name && (
            <small className="error-message">{errors.other_name.message}</small>
          )}
        </div>
      </div>

      <div className="row flex gap-3">
        <div className="flex flex-column col-6 pl-0 pr-0  gap-2">
          <label htmlFor="surname" className="flex align-items-center gap-1">
            Surname(s)*
            <span id="surname-info" className="pi pi-info-circle"></span>
            <Tooltip
              target="#surname-info"
              content="This is usually a family name or last name. If employee has more than one, include them here."
              position="right"
              className="custom-tooltip tooltip-custom-format"
            />
          </label>
          <Controller
            name="surname"
            control={control}
            rules={{
              required: "Surname is required",
              pattern: {
                value: /^[A-Za-z\s]+$/,
                message: "Special characters are not allowed.",
              },
            }}
            render={({ field }) => (
              <InputText
                {...field}
                placeholder="Enter surname(s)"
                type="text"
                className={`input-field appearance-none outline-none w-full ${
                  errors.surname ? "error" : ""
                }`}
                disabled={isDisabled}
              />
            )}
          />
          {errors.surname && (
            <small className="error-message">{errors.surname.message}</small>
          )}
        </div>
        <div className="flex flex-column col-6 pl-0 pr-0  gap-2">
          <label
            htmlFor="preferred_name"
            className="flex align-items-center gap-1"
          >
            Preferred name(s)
            <span id="preferred-name-info" className="pi pi-info-circle"></span>
            <Tooltip
              target="#preferred-name-info"
              content="If the employee wants to be called by a different name than their given name, put that here."
              position="right"
              className="custom-tooltip tooltip-custom-format"
            />
          </label>
          <Controller
            name="preferred_name"
            control={control}
            rules={{
              pattern: {
                value: /^[A-Za-z\s]+$/,
                message: "Special characters are not allowed.",
              },
            }}
            render={({ field }) => (
              <InputText
                {...field}
                placeholder="Preferred name or nickname"
                type="text"
                className={`input-field appearance-none outline-none w-full ${
                  errors.preferred_name ? "error" : ""
                }`}
                disabled={isDisabled}
              />
            )}
          />
          {errors.preferred_name && (
            <small className="error-message">
              {errors.preferred_name.message}
            </small>
          )}
        </div>
      </div>
      <DirectorMatchingResult
        data={directorsArr?.data || []}
        loading={directorsArr?.loading || false}
        setIsSubmittedSuccessfully={setIsSubmittedSuccessfully}
      />
      {![UserRole.LSP_MANAGER, UserRole.LSP_USER].includes(role) && (
        <>
          <div className="row flex gap-3">
            <div className="flex flex-column col-12 pl-0 pr-0  gap-2">
              <label htmlFor="email" className="flex align-items-center gap-1">
                Contact email*
              </label>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: "Contact email is required",
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$/,
                    message: "Contact Email is invalid.",
                  },
                }}
                render={({ field }) => (
                  <InputText
                    {...field}
                    placeholder="Enter email address"
                    className={`input-field appearance-none outline-none w-full ${
                      errors.email ? "error" : ""
                    }`}
                    disabled={isDisabled}
                  />
                )}
              />
              {errors.email && (
                <small className="error-message">{errors.email.message}</small>
              )}
            </div>
          </div>

          <div className="row flex gap-3">
            <div className="flex flex-column col-12 pl-0 pr-0 gap-2">
              <label
                htmlFor="phone_dode"
                className="flex align-items-center gap-1"
              >
                Phone number*
              </label>
              <div className="flex">
                <div className="col-3 pl-0 pr-0">
                  <Controller
                    name="phone_code"
                    control={control}
                    rules={{
                      required: "Phone Code is required",
                    }}
                    render={({ field }) => (
                      <Dropdown
                        {...field}
                        placeholder="Country code"
                        optionLabel="label"
                        options={phoneCodeOptions}
                        disabled={isDisabled}
                        className={`officer-dropdown-field w-full ${errors.phone_code ? "error" : ""}`}
                      />
                    )}
                  />
                </div>
                <div className="col-9 pl-0 pr-0">
                  <Controller
                    name="phone_number"
                    control={control}
                    rules={{
                      required: "Phone number is required",
                      pattern: {
                        value: /^[0-9]+$/,
                        message: "Phone number must contain only digits.",
                      },
                    }}
                    render={({ field }) => (
                      <InputText
                        {...field}
                        placeholder="Enter phone number"
                        className={`input-field appearance-none outline-none w-full ${
                          errors.phone_number ? "error" : ""
                        }`}
                        disabled={isDisabled}
                      />
                    )}
                  />
                  {errors.phone_number && (
                    <small className="error-message">
                      {errors.phone_number.message}
                    </small>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Audit */}
      {isAuditSidebarVisible && (
        <AuditSidebar
          heading="Director Information"
          endpoint={API_ROUTES_V2.FETCH_DIRECTOR_AUDIT}
          id={directorId}
          objectType="director"
          setSidebarToggle={setIsAuditSidebarVisible}
          sidebarToggle={isAuditSidebarVisible}
          labels={auditLabels}
          tab={"info"}
        />
      )}
    </div>
  );
};

export default DirectorInformationForm;
